import React, { useState } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { DisqualifyParticipant } from './../../../../utilities/BL/OptimoOpticas';

/**
 * Modal de confirmación y captura de motivo de descalificación justificada
 * para participantes de la dinámica 'Rasca y Gana - Optimo Ópticas'.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Visibilidad del modal.
 * @param {Object} props.participant - Datos del participante seleccionado.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} props.onDisqualified - Callback al concretar la descalificación.
 */
export function OptimoOpticasDisqualifyModal({ isOpen, participant, onClose, onDisqualified }) {
    const { showMessage } = useFloatMessage();
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen || !participant) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = reason.trim();
        if (!trimmed) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Por favor indica el motivo fundado de la descalificación.'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await DisqualifyParticipant(participant.id, trimmed);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `El participante ${participant.name} ha sido descalificado con éxito.`
                });
                if (onDisqualified) onDisqualified(participant.id, trimmed);
                setReason('');
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al descalificar al participante.'
                });
            }
        } catch (error) {
            console.error("Error en OptimoOpticasDisqualifyModal:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión con el servidor al procesar la descalificación.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1070 }}
            onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}
        >
            <div className="modal-dialog modal-md modal-dialog-centered">
                <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden">
                    <div className="modal-header bg-danger text-white border-0 px-4 py-3">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
                            <i className="bi bi-shield-x fs-5"></i>
                            Descalificar Participante
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close btn-close-white" 
                            aria-label="Close"
                            disabled={isSubmitting}
                            onClick={onClose}
                        ></button>
                    </div>

                    <div className="modal-body p-4 bg-light">
                        <div className="card border-0 shadow-sm p-3 bg-white rounded-3 mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="fw-bold text-dark fs-6">{participant.name}</span>
                                {participant.couponCode && (
                                    <span className="badge bg-danger-subtle text-danger font-monospace px-2.5 py-1 fw-bold">
                                        {participant.couponCode}
                                    </span>
                                )}
                            </div>
                            <div className="small text-secondary mb-1 d-flex align-items-center gap-1.5">
                                <i className="bi bi-envelope text-muted"></i>
                                <span>{participant.email}</span>
                            </div>
                            <div className="small text-secondary d-flex align-items-center gap-1.5">
                                <i className="bi bi-telephone text-muted"></i>
                                <span>{participant.phone}</span>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} id="optimoDisqualifyForm">
                            <label className="form-label fw-semibold text-dark small mb-1">
                                Motivo de descalificación <span className="text-danger">*</span>
                            </label>
                            <textarea 
                                className="form-control mb-2"
                                rows="3"
                                placeholder="Ej: No cumple con los requisitos oficiales / Registro duplicado / Conducta inapropiada..."
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={isSubmitting}
                                required
                                autoFocus
                            ></textarea>
                            <small className="text-muted d-block" style={{ fontSize: '0.78rem', lineHeight: '1.4' }}>
                                Este motivo quedará registrado en la bitácora de auditoría y en el reporte ejecutivo oficial. Si es necesario, podrás restituir al participante más adelante desde la tabla.
                            </small>
                        </form>
                    </div>

                    <div className="modal-footer bg-white border-top px-4 py-3 d-flex justify-content-between">
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary px-4 fw-semibold"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            form="optimoDisqualifyForm"
                            className="btn btn-danger px-4 fw-bold d-flex align-items-center gap-2"
                            disabled={isSubmitting || !reason.trim()}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Descalificando...</span>
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-slash-circle"></i>
                                    <span>Confirmar Descalificación</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OptimoOpticasDisqualifyModal;
